/**
 * Bank format definitions for CSV import.
 *
 * Each format describes:
 *  - how to detect the bank from the CSV headers
 *  - the CSV delimiter
 *  - default column → transaction field mappings
 *  - optional value transformers (e.g. German decimal format, date format)
 */

export type TransactionField =
  | "amount"
  | "paymentDate"
  | "name"
  | "iban"
  | "description"
  | "currency"
  | "ignore";

/**
 * Decimal-separator convention used for amount cells in the CSV.
 *  - "german"        → "1.234,56"  (period thousands, comma decimal)
 *  - "international" → "1,234.56"  (comma thousands, period decimal)
 */
export type AmountFormat = "german" | "international";

/**
 * Date format used for date cells in the CSV. Stored ISO is always "YYYY-MM-DD".
 *  - "iso"      → "YYYY-MM-DD"               (passthrough)
 *  - "de-dot"   → "DD.MM.YYYY" or "DD.MM.YY"
 *  - "de-slash" → "DD/MM/YYYY" or "DD/MM/YY"
 *  - "de-dash"  → "DD-MM-YYYY"               (also tolerates trailing time, e.g. Qonto)
 */
export type DateFormat = "iso" | "de-dot" | "de-slash" | "de-dash";

export interface ColumnMapping {
  /** CSV column header as it appears in the file */
  csvColumn: string;
  /** Target transaction field */
  targetField: TransactionField;
  /**
   * Decimal convention for this column. Only meaningful when targetField === "amount".
   * Pre-filled from the detected bank format or auto-detected from sample values;
   * the user can override it in the mapping step.
   */
  amountFormat?: AmountFormat;
  /**
   * Date format for this column. Only meaningful when targetField === "paymentDate".
   * Pre-filled from the detected bank format or auto-detected from sample values;
   * the user can override it in the mapping step.
   */
  dateFormat?: DateFormat;
}

export interface BankFormat {
  label: string;
  delimiter: string;
  /** Subset of headers that uniquely identify this bank's export */
  detectionHeaders: string[];
  defaultMappings: ColumnMapping[];
  /** Default amount format used to pre-fill the per-column dropdown */
  defaultAmountFormat: AmountFormat;
  /** Default date format used to pre-fill the per-column dropdown */
  defaultDateFormat: DateFormat;
  /**
   * Optional post-processing applied to the parsed amount (in cents).
   * Used e.g. by Ausgaben to flip the sign of expense amounts.
   */
  amountSign?: 1 | -1;
}

// ── Amount parsers ────────────────────────────────────────────────────────

/** Strip currency symbols and whitespace shared by both formats. */
const stripCurrency = (raw: string): string =>
  raw.replace(/[€$£]/g, "").replace(/\s/g, "");

// Strict shapes: optional sign, digits with optional thousands grouping,
// optional 1–2 decimal digits. Anchored so trailing garbage (e.g. "21,95foo")
// is rejected and surfaces as a row error.
const GERMAN_AMOUNT_RE = /^-?(?:\d{1,3}(?:\.\d{3})*|\d+)(?:,\d{1,2})?$/;
const INTERNATIONAL_AMOUNT_RE = /^-?(?:\d{1,3}(?:,\d{3})*|\d+)(?:\.\d{1,2})?$/;

/** German decimal format: "1.234,56" or "€ 1 234,56" → 123456 (cents) */
export const parseGermanAmount = (raw: string): number => {
  const cleaned = stripCurrency(raw).trim();
  if (!GERMAN_AMOUNT_RE.test(cleaned)) return NaN;
  const normalized = cleaned.replace(/\./g, "").replace(",", ".");
  return Math.round(Number(normalized) * 100);
};

/** International decimal format: "1,234.56" or "12.34" → cents */
export const parseInternationalAmount = (raw: string): number => {
  const cleaned = stripCurrency(raw).trim();
  if (!INTERNATIONAL_AMOUNT_RE.test(cleaned)) return NaN;
  const normalized = cleaned.replace(/,/g, "");
  return Math.round(Number(normalized) * 100);
};

export const parseAmountWithFormat = (
  raw: string,
  format: AmountFormat
): number =>
  format === "german" ? parseGermanAmount(raw) : parseInternationalAmount(raw);

/**
 * Auto-detect the decimal separator from sample cells.
 * Strategy:
 *  - If any cell has both "." and ",", the rightmost one is the decimal separator.
 *  - Else, grouped-integer shape (e.g. "1.234" / "1,234") disambiguates by the
 *    grouping char — this catches German integer amounts that would otherwise
 *    be misread as international decimals.
 *  - Else, fall back to counting "tail" decimals (e.g. ",56" vs ".56").
 *  - Default → international.
 */
export const detectAmountFormat = (samples: string[]): AmountFormat => {
  const cells = samples
    .map((s) => stripCurrency(s ?? "").trim())
    .filter((s) => s.length > 0);

  for (const cell of cells) {
    const lastDot = cell.lastIndexOf(".");
    const lastComma = cell.lastIndexOf(",");
    if (lastDot !== -1 && lastComma !== -1) {
      return lastComma > lastDot ? "german" : "international";
    }
  }

  // Grouped integers: "1.234" / "12.000" → german thousands separator;
  //                   "1,234" / "12,000" → international thousands separator.
  const germanGrouped = /^-?\d{1,3}(?:\.\d{3})+(?:,\d{1,2})?$/;
  const intlGrouped = /^-?\d{1,3}(?:,\d{3})+(?:\.\d{1,2})?$/;
  for (const cell of cells) {
    if (germanGrouped.test(cell)) return "german";
    if (intlGrouped.test(cell)) return "international";
  }

  const germanTail = /,\d{1,2}$/;
  const intlTail = /\.\d{1,2}$/;
  let germanHits = 0;
  let intlHits = 0;
  for (const cell of cells) {
    if (germanTail.test(cell)) germanHits++;
    if (intlTail.test(cell)) intlHits++;
  }
  if (germanHits > intlHits) return "german";
  if (intlHits > germanHits) return "international";
  return "international";
};

// ── Date parsers ──────────────────────────────────────────────────────────

const isValidYmd = (iso: string): boolean => {
  const [y, mo, d] = iso.split("-").map(Number);
  if (mo < 1 || mo > 12 || d < 1 || d > 31 || y < 1900 || y > 2999) {
    return false;
  }
  // Round-trip through Date to reject impossible dates (e.g. Feb 31, Apr 31)
  const dt = new Date(Date.UTC(y, mo - 1, d));
  return (
    dt.getUTCFullYear() === y &&
    dt.getUTCMonth() === mo - 1 &&
    dt.getUTCDate() === d
  );
};

/** Parse a date string with the given format. Returns ISO "YYYY-MM-DD" or null. */
export const parseDateWithFormat = (
  raw: string,
  format: DateFormat
): string | null => {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  // Strip any trailing time component (e.g. Qonto's "30-05-2025 01:42:08")
  const datePart = trimmed.split(/[ T]/)[0];

  let m: RegExpMatchArray | null;
  let iso: string | null = null;
  if (format === "iso") {
    m = datePart.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (m) iso = `${m[1]}-${m[2].padStart(2, "0")}-${m[3].padStart(2, "0")}`;
  } else if (format === "de-dot") {
    m = datePart.match(/^(\d{1,2})\.(\d{1,2})\.(\d{2}|\d{4})$/);
    if (m) {
      const fullYear = m[3].length === 2 ? `20${m[3]}` : m[3];
      iso = `${fullYear}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`;
    }
  } else if (format === "de-slash") {
    m = datePart.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/);
    if (m) {
      const fullYear = m[3].length === 2 ? `20${m[3]}` : m[3];
      iso = `${fullYear}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`;
    }
  } else if (format === "de-dash") {
    m = datePart.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
    if (m) iso = `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`;
  }

  if (!iso || !isValidYmd(iso)) return null;
  return iso;
};

const ALL_DATE_FORMATS: DateFormat[] = ["iso", "de-dot", "de-slash", "de-dash"];

/**
 * Auto-detect the date format from sample cells. Returns the format that
 * strictly parses the most non-empty samples; falls back to "iso" on a tie or
 * empty input.
 */
export const detectDateFormat = (samples: string[]): DateFormat => {
  const cells = samples
    .map((s) => (s ?? "").trim())
    .filter((s) => s.length > 0);
  if (cells.length === 0) return "iso";

  let best: DateFormat = "iso";
  let bestHits = -1;
  for (const format of ALL_DATE_FORMATS) {
    let hits = 0;
    for (const cell of cells) {
      if (parseDateWithFormat(cell, format) !== null) hits++;
    }
    if (hits > bestHits) {
      bestHits = hits;
      best = format;
    }
  }
  return best;
};

/**
 * Try every known format, return the first ISO match.
 * Used as a fallback when no per-column dateFormat has been set.
 */
export const parseDateBestEffort = (raw: string): string | null => {
  for (const format of ALL_DATE_FORMATS) {
    const iso = parseDateWithFormat(raw, format);
    if (iso) return iso;
  }
  return null;
};

// ── Bank Format Definitions ────────────────────────────────────────────────

export const N26_FORMAT: BankFormat = {
  label: "N26",
  delimiter: ",",
  detectionHeaders: [
    "Booking Date",
    "Value Date",
    "Partner Name",
    "Amount (EUR)",
  ],
  defaultMappings: [
    { csvColumn: "Booking Date", targetField: "paymentDate" },
    { csvColumn: "Value Date", targetField: "ignore" },
    { csvColumn: "Partner Name", targetField: "name" },
    { csvColumn: "Partner Iban", targetField: "iban" },
    { csvColumn: "Type", targetField: "ignore" },
    { csvColumn: "Payment Reference", targetField: "description" },
    { csvColumn: "Account Name", targetField: "ignore" },
    { csvColumn: "Amount (EUR)", targetField: "amount" },
    { csvColumn: "Original Amount", targetField: "ignore" },
    { csvColumn: "Original Currency", targetField: "ignore" },
    { csvColumn: "Exchange Rate", targetField: "ignore" },
  ],
  defaultAmountFormat: "international",
  defaultDateFormat: "iso",
};

export const SPARKASSE_FORMAT: BankFormat = {
  label: "Sparkasse",
  delimiter: ";",
  detectionHeaders: [
    "Auftragskonto",
    "Buchungstag",
    "Beguenstigter/Zahlungspflichtiger",
    "Betrag",
  ],
  defaultMappings: [
    { csvColumn: "Auftragskonto", targetField: "ignore" },
    { csvColumn: "Buchungstag", targetField: "paymentDate" },
    { csvColumn: "Valutadatum", targetField: "ignore" },
    { csvColumn: "Buchungstext", targetField: "ignore" },
    { csvColumn: "Verwendungszweck", targetField: "description" },
    { csvColumn: "Beguenstigter/Zahlungspflichtiger", targetField: "name" },
    { csvColumn: "Kontonummer/IBAN", targetField: "iban" },
    { csvColumn: "BIC (SWIFT-Code)", targetField: "ignore" },
    { csvColumn: "Betrag", targetField: "amount" },
    { csvColumn: "Waehrung", targetField: "currency" },
    { csvColumn: "Info", targetField: "ignore" },
    { csvColumn: "Kategorie", targetField: "ignore" },
  ],
  defaultAmountFormat: "german",
  defaultDateFormat: "de-dot",
};

/**
 * Generic invoice/income export (Einnahmen) with Euro-prefixed amounts.
 * Detects uniquely by "Rechnungsempfänger".
 * Example: "04/01/23;Digistore 24;€ 221,55;..."
 * Amounts are positive (income). Tag: "CSV Import" (not bank-specific).
 */
export const EINNAHMEN_FORMAT: BankFormat = {
  label: "Einnahmen (Datum / € Betrag)",
  delimiter: ";",
  detectionHeaders: ["Datum", "Rechnungsempfänger", "Bruttobetrag"],
  defaultMappings: [
    { csvColumn: "Datum", targetField: "paymentDate" },
    { csvColumn: "Rechnungsempfänger", targetField: "name" },
    { csvColumn: "Bruttobetrag", targetField: "amount" },
    { csvColumn: "Nettobetrag", targetField: "ignore" },
    { csvColumn: "Steuersatz", targetField: "ignore" },
    { csvColumn: "Steuer", targetField: "ignore" },
    { csvColumn: "Einnahmen Kategorie", targetField: "ignore" },
  ],
  defaultAmountFormat: "german",
  defaultDateFormat: "de-slash",
};

/**
 * Generic invoice/expenses export (Ausgaben) with Euro-prefixed amounts.
 * Detects uniquely by "Rechnungsteller" (vendor column, unlike Einnahmen's "Rechnungsempfänger").
 * Amounts are stored as negative (outgoing expense). Tag: "CSV Import" (not bank-specific).
 */
export const AUSGABEN_FORMAT: BankFormat = {
  label: "Ausgaben (Datum / € Betrag)",
  delimiter: ";",
  detectionHeaders: ["Datum", "Rechnungsteller", "Bruttobetrag"],
  defaultMappings: [
    { csvColumn: "Datum", targetField: "paymentDate" },
    { csvColumn: "Rechnungsteller", targetField: "name" },
    { csvColumn: "Bruttobetrag", targetField: "amount" },
    { csvColumn: "Nettobetrag", targetField: "ignore" },
    { csvColumn: "Steuersatz", targetField: "ignore" },
    { csvColumn: "Steuer", targetField: "ignore" },
    { csvColumn: "Vermerk", targetField: "description" },
    { csvColumn: "Ansatz Vorsteuer", targetField: "ignore" },
    { csvColumn: "Ausgaben Kategorie", targetField: "ignore" },
  ],
  defaultAmountFormat: "german",
  defaultDateFormat: "de-slash",
  // Ausgaben are outgoing — flip the sign so the amount is stored as negative cents
  amountSign: -1,
};

/**
 * Qonto bank export (German headers, semicolon-delimited).
 * Amount column "Gesamtbetrag (inkl. MwSt.)" is signed (negative = expense).
 * Date format: "DD-MM-YYYY HH:MM:SS"
 */
export const QONTO_FORMAT: BankFormat = {
  label: "Qonto",
  delimiter: ";",
  detectionHeaders: [
    "Transaktions-ID",
    "Name der Gegenpartei",
    "Abrechnungsdatum (UTC)",
  ],
  defaultMappings: [
    { csvColumn: "Abrechnungsdatum (UTC)", targetField: "ignore" },
    { csvColumn: "Abrechnungstag (lokal)", targetField: "ignore" },
    { csvColumn: "Datum des Vorgangs (UTC)", targetField: "paymentDate" },
    { csvColumn: "Datum des Vorgangs (lokal)", targetField: "ignore" },
    { csvColumn: "Gesamtbetrag (inkl. MwSt.)", targetField: "amount" },
    { csvColumn: "Soll", targetField: "ignore" },
    { csvColumn: "Haben", targetField: "ignore" },
    { csvColumn: "Kontostand", targetField: "ignore" },
    { csvColumn: "Währung", targetField: "currency" },
    { csvColumn: "Gesamtbetrag (inkl. MwSt.) (lokal)", targetField: "ignore" },
    { csvColumn: "Währung (lokal)", targetField: "ignore" },
    { csvColumn: "MwSt.-Betrag insgesamt", targetField: "ignore" },
    { csvColumn: "Gesamtbetrag (ohne MwSt.)", targetField: "ignore" },
    { csvColumn: "Name der Gegenpartei", targetField: "name" },
    { csvColumn: "IBAN der Gegenpartei", targetField: "iban" },
    { csvColumn: "Referenz", targetField: "description" },
    { csvColumn: "Transaktions-ID", targetField: "ignore" },
    { csvColumn: "Status", targetField: "ignore" },
    { csvColumn: "Zahlungsart", targetField: "ignore" },
    { csvColumn: "Kartenname", targetField: "ignore" },
    { csvColumn: "Veranlasst von", targetField: "ignore" },
    { csvColumn: "E-Mail-Adresse des Initiators", targetField: "ignore" },
    { csvColumn: "Team", targetField: "ignore" },
    { csvColumn: "Anhang", targetField: "ignore" },
    { csvColumn: "Anhang erforderlich", targetField: "ignore" },
    { csvColumn: "Anhang verloren gegangen", targetField: "ignore" },
    { csvColumn: "Notiz", targetField: "ignore" },
    { csvColumn: "Bank", targetField: "ignore" },
    { csvColumn: "Konto ID", targetField: "ignore" },
    { csvColumn: "Kontoname", targetField: "ignore" },
    { csvColumn: "Konto-IBAN", targetField: "ignore" },
    { csvColumn: "Cashflow-Kategorie", targetField: "ignore" },
    { csvColumn: "Cashflow-Unterkategorie", targetField: "ignore" },
  ],
  defaultAmountFormat: "german",
  defaultDateFormat: "de-dash",
};

/**
 * HVB (HypoVereinsbank) export.
 * Exported as UTF-16 LE; the parseCsvFile function decodes it before parsing.
 * Uses ASCII transliterations for German umlauts (ä→ae, ü→ue, ö→oe).
 * Amount column "Betrag" uses German decimal format.
 * Date column "Buchungsdatum" uses "DD.MM.YYYY" format.
 */
export const HVB_FORMAT: BankFormat = {
  label: "HypoVereinsbank (HVB)",
  delimiter: ";",
  detectionHeaders: [
    "Kontonummer",
    "Buchungsdatum",
    "Verwendungszweck",
    "Betrag",
    "Waehrung",
  ],
  defaultMappings: [
    { csvColumn: "Kontonummer", targetField: "ignore" },
    { csvColumn: "Buchungsdatum", targetField: "paymentDate" },
    { csvColumn: "Valuta", targetField: "ignore" },
    { csvColumn: "Verwendungszweck", targetField: "name" },
    { csvColumn: "Betrag", targetField: "amount" },
    { csvColumn: "Waehrung", targetField: "currency" },
  ],
  defaultAmountFormat: "german",
  defaultDateFormat: "de-dot",
};

export const KNOWN_FORMATS: BankFormat[] = [
  N26_FORMAT,
  SPARKASSE_FORMAT,
  EINNAHMEN_FORMAT,
  AUSGABEN_FORMAT,
  QONTO_FORMAT,
  HVB_FORMAT,
];

/**
 * Detect the bank format from the CSV headers.
 * Returns null if no known format matches — the user will need to map columns manually.
 */
export const detectBankFormat = (headers: string[]): BankFormat | null => {
  const headerSet = new Set(headers.map((h) => h.trim()));
  for (const format of KNOWN_FORMATS) {
    const matched = format.detectionHeaders.every((h) => headerSet.has(h));
    if (matched) return format;
  }
  return null;
};

/**
 * Heuristic header→field mapping used when no bank format is detected.
 * Order matters: earlier entries win when more than one matches a header,
 * and each target field is assigned at most once across all headers.
 *
 * Each entry's `match` is checked case-insensitively against the trimmed header.
 */
const HEADER_HEURISTICS: { match: string[]; field: TransactionField }[] = [
  {
    field: "paymentDate",
    match: [
      "datum",
      "date",
      "payment date",
      "booking date",
      "transaction date",
      "buchungstag",
      "buchungsdatum",
      "value date",
      "valuta",
      "valutadatum",
    ],
  },
  {
    field: "amount",
    match: [
      "betrag",
      "amount",
      "bruttobetrag",
      "gesamtbetrag",
      "value",
      "amount (eur)",
    ],
  },
  {
    field: "currency",
    match: ["währung", "waehrung", "currency"],
  },
  {
    field: "iban",
    match: ["iban", "partner iban", "kontonummer/iban", "iban der gegenpartei"],
  },
  {
    field: "name",
    match: [
      "name",
      "partner name",
      "karteninhaber",
      "rechnungsempfänger",
      "rechnungsteller",
      "beguenstigter/zahlungspflichtiger",
      "name der gegenpartei",
    ],
  },
  {
    field: "description",
    match: [
      "description",
      "beschreibung",
      "verwendungszweck",
      "payment reference",
      "referenz",
      "vermerk",
    ],
  },
];

/** Suggest a target field for a header, ignoring fields that are already taken. */
const suggestField = (
  header: string,
  taken: Set<TransactionField>
): TransactionField => {
  const normalized = header.trim().toLowerCase();
  for (const { field, match } of HEADER_HEURISTICS) {
    if (taken.has(field)) continue;
    if (match.includes(normalized)) return field;
  }
  return "ignore";
};

/**
 * Build default column mappings for an arbitrary set of headers.
 *
 * When a bank format is detected, its `defaultMappings` are used.
 * Otherwise we fall back to a per-header heuristic that catches common
 * DE/EN names (e.g. "Datum"→paymentDate, "Betrag"→amount).
 *
 * When a column ends up mapped to "amount" or "paymentDate", its format hint
 * (`amountFormat` / `dateFormat`) is pre-filled from the detected bank format's
 * default — or, if no format was detected, by auto-detecting from the sample rows.
 */
export const buildDefaultMappings = (
  headers: string[],
  format: BankFormat | null,
  sampleRows: Record<string, string>[] = []
): ColumnMapping[] => {
  const withFormatHints = (mapping: ColumnMapping): ColumnMapping => {
    if (mapping.targetField === "amount") {
      const amountFormat = format
        ? format.defaultAmountFormat
        : detectAmountFormat(sampleRows.map((r) => r[mapping.csvColumn] ?? ""));
      return { ...mapping, amountFormat };
    }
    if (mapping.targetField === "paymentDate") {
      const dateFormat = format
        ? format.defaultDateFormat
        : detectDateFormat(sampleRows.map((r) => r[mapping.csvColumn] ?? ""));
      return { ...mapping, dateFormat };
    }
    return mapping;
  };

  if (format) {
    const headerSet = new Set(headers);
    // Only include default mappings for columns that actually exist in the file
    const existing = format.defaultMappings
      .filter((m) => headerSet.has(m.csvColumn))
      .map(withFormatHints);
    // Add any columns the format doesn't know about, defaulting to ignore
    const knownColumns = new Set(
      format.defaultMappings.map((m) => m.csvColumn)
    );
    const extra = headers
      .filter((h) => !knownColumns.has(h))
      .map<ColumnMapping>((h) => ({ csvColumn: h, targetField: "ignore" }));
    return [...existing, ...extra];
  }

  // No bank format — apply per-header heuristics, each target field at most once
  const taken = new Set<TransactionField>();
  return headers.map<ColumnMapping>((h) => {
    const targetField = suggestField(h, taken);
    if (targetField !== "ignore") taken.add(targetField);
    return withFormatHints({ csvColumn: h, targetField });
  });
};
